import { useMockableViewModel } from '@/hooks'
import { useNavigation } from '@/app/hooks'
import { VehicleChargeSchedule } from '@/types'
import { useState } from 'react'
import useVehicleSettingsManager from '@/hooks/useVehicleSettingsManager'
import { useTripsForVehicle } from '@/reducers/trips'
import { RequestStatus } from '@/request/types'
import { isTesla } from '@/utils/vehicles/car'
import utils from './ChargeSettingsCardHelpers'
import { cloneDeep } from 'lodash'
import useNavigateToTeslaKeyPairing from '@/app/hooks/useNavigateToTeslaKeyPairing'
import toExternalLink from '@/utils/to-external-link'
import { match } from 'ts-pattern'
import { generateLimitsText } from '@/types/chargeSchedule/chargeSchedule.utils'
import { vehicleActiveChargeSettingsCollection } from '@/reducers/vehicleActiveChargeSettings'
import { VehicleActiveChargeSettings } from '@/types/activeChargeSettings'

function useViewModel() {
  const {
    vehicleId,
    vehicle,
    chargeSchedule,
    debouncedSave,
    utilityAlert,
    utilityChargeControlOverride,
  } = useVehicleSettingsManager()

  // Also honor the active-charge-settings serializer: when a program prescribes
  // `allow_charging_control` on (DEV-6020), force the charge-control toggle on
  // and disabled, same as the existing enrollment-group override. ORed together
  // so existing behavior is preserved and the serializer is an additional source.
  const { data: activeSettings } =
    vehicleActiveChargeSettingsCollection.useFetch(String(vehicleId))
  const allowChargingControlSetting = (
    activeSettings[0] as VehicleActiveChargeSettings | undefined
  )?.allow_charging_control
  const serializerForcesChargeControl =
    !!allowChargingControlSetting?.is_program_prescribed &&
    allowChargingControlSetting.value === true
  const forceChargeControl =
    utilityChargeControlOverride || serializerForcesChargeControl
  const navigation = useNavigation()
  const { trips, status: tripStatus } = useTripsForVehicle(vehicleId)
  const [settingsEnabled, setSettingsEnabled] = useState<boolean>(
    chargeSchedule?.allow_charging_control ?? false,
  )

  const [localChargeSchedule, setLocalChargeSchedule] = useState(
    cloneDeep(chargeSchedule),
  )

  const {
    isLoading: isLoadingNavigateToTesla,
    error: navigateToTeslaKeyPairingError,
    navigate: navigateToTeslaKeyPairing,
  } = useNavigateToTeslaKeyPairing()

  const vehicleCanCharge = utils.vehicleCanCharge(vehicle)
  const userEnabledChargeControl =
    chargeSchedule?.allow_charging_control ?? false
  const limitsSubtitle = generateLimitsText(chargeSchedule)
  const goalsSubtitle = utils.generateGoalsText(chargeSchedule)
  const scheduleSubtitle = utils.generateScheduleSubtitle(chargeSchedule)
  const tripsSubtitle = utils.generateTripsText(trips)
  const alert = utils.getAlert(utilityAlert, vehicle, chargeSchedule)
  const disableOptiwattCharging = forceChargeControl || !vehicleCanCharge
  const vehicleMake = vehicle?.car?.car_model?.make

  const deviceNav = (path: string) => {
    navigation.push(`/devices/vehicle/${vehicleId}/${path}`)
  }

  const connectionSubtitleClicked = () => {
    if (vehicle && isTesla(vehicle)) {
      if (vehicle.car?.public_key_not_attached_at) {
        navigateToTeslaKeyPairing(vehicle)
        return
      }
      navigation.push('/tesla-authentication/edit-permissions')
      return
    }

    navigation.push('/connect-device/charger')
  }

  const onToggleChargeControl = (chargeSchedule: VehicleChargeSchedule) => {
    if (!chargeSchedule || !localChargeSchedule) {
      return
    }

    const updatedSchedule = {
      ...localChargeSchedule,
      allow_charging_control: !localChargeSchedule.allow_charging_control,
    }
    setLocalChargeSchedule(updatedSchedule)
    setSettingsEnabled(updatedSchedule.allow_charging_control)
    debouncedSave(updatedSchedule)
  }

  const failedConnectionText = vehicle ? utils.getConnectionText(vehicle) : ''

  const onAlertLinkPress = () => {
    const { urlOrPath } = alert?.link ?? {}
    if (!urlOrPath) return
    if (urlOrPath.startsWith('http')) {
      return toExternalLink(urlOrPath)
    } else {
      return navigation.push(urlOrPath)
    }
  }

  const controlledViaChargeLimit = match(vehicle?.car?.control_interface_type)
    .with('limit_based', () => localChargeSchedule?.allow_charging_control)
    .otherwise(() => false)

  return {
    isLoading:
      !vehicle ||
      !chargeSchedule ||
      tripStatus == RequestStatus.Loading ||
      isLoadingNavigateToTesla,
    vehicleAuthenticationInactive: vehicle?.authentication_inactive,
    deviceNav,
    connectionSubtitleClicked,
    onToggleChargeControl,
    limitsSubtitle,
    goalsSubtitle,
    scheduleSubtitle,
    tripsSubtitle,
    chargeSchedule: localChargeSchedule,
    vehicleCanCharge,
    forceChargeControl,
    disableOptiwattCharging,
    settingsEnabled: settingsEnabled || forceChargeControl,
    alert,
    onAlertLinkPress,
    failedConnectionText,
    controlledViaChargeLimit,
    vehicleMake,
    userEnabledChargeControl,
  }
}

function useMockViewModel() {
  return {
    isLoading: false,
    vehicleAuthenticationInactive: false,
    deviceNav: () => alert('Nav clicked!'),
    onToggleChargeControl: () => alert('Charge Control status toggled!'),
    connectionSubtitleClicked: () => alert('EVSE clicked'),
    limitsSubtitle: 'Min - 20%, Max - 65%',
    goalsSubtitle: 'Solar Surplus',
    vehicleCanCharge: false,
    scheduleSubtitle: 'Weekdays: Ready By time: 9:00 AM EST',
    tripsSubtitle: 'Upcoming trip: July 31 at 12:00 PM EST',
    chargeSchedule: undefined,
    forceChargeControl: false,
    disableOptiwattCharging: true,
    settingsEnabled: true,
    alert: {
      message: 'Utility alert!',
      variant: 'info',
    } as const,
    missingPermissions: false,
    failedConnectionText: 'Connect EVSE',
    onAlertLinkPress: () => {},
    controlledViaChargeLimit: false,
    vehicleMake: 'Tesla',
    userEnabledChargeControl: false,
  }
}

export default useMockableViewModel({ useViewModel, useMockViewModel })
